import { useEffect, useRef, useState } from "react";
import { Share2, Link2, Check, Smartphone } from "lucide-react";
import { markShared } from "../lib/api";

// Brand SVGs kept inline so we never depend on an icon library that
// might rebrand. They are intentionally simple and currentColor-driven so
// they match the surrounding text.
const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" {...props}>
    <path d="M19.05 4.91A10 10 0 0 0 4.42 18.4L3 23l4.7-1.4A10 10 0 0 0 22 12a9.9 9.9 0 0 0-2.95-7.09zM12 20.1a8.1 8.1 0 0 1-4.13-1.13l-.3-.18-2.79.83.84-2.72-.2-.31A8.1 8.1 0 1 1 20.1 12 8.07 8.07 0 0 1 12 20.1zm4.45-6.06c-.24-.12-1.45-.72-1.67-.8s-.39-.12-.55.13-.63.8-.78.96-.28.18-.52.06a6.7 6.7 0 0 1-1.97-1.22 7.4 7.4 0 0 1-1.37-1.7c-.14-.25 0-.38.11-.5s.25-.28.37-.42a1.66 1.66 0 0 0 .25-.42.46.46 0 0 0 0-.43c-.06-.12-.55-1.33-.75-1.83s-.4-.42-.55-.43h-.47a.9.9 0 0 0-.65.3 2.74 2.74 0 0 0-.86 2c0 1.2.88 2.37 1 2.54s1.73 2.66 4.2 3.73a14 14 0 0 0 1.41.52 3.41 3.41 0 0 0 1.55.1 2.53 2.53 0 0 0 1.66-1.17 2.06 2.06 0 0 0 .14-1.17c-.06-.1-.21-.18-.45-.3z" />
  </svg>
);

const XIcon = (props) => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor" {...props}>
    <path d="M18.244 2H21.5l-7.55 8.625L23 22h-6.825l-5.34-6.978L4.7 22H1.43l8.075-9.226L1 2h7l4.83 6.39L18.244 2zm-2.41 18h1.83L7.265 4H5.34l10.494 16z" />
  </svg>
);

// The channels array is intentionally ordered. The first channel is the
// native share API on mobile; on desktop we hide it. Adding Facebook or
// Email later is a one-line addition — that's the whole point.
const CHANNELS = [
  {
    key: "native",
    label: "Share via device",
    icon: Smartphone,
    onlyIf: () => typeof navigator !== "undefined" && !!navigator.share,
    handler: async ({ url, title, text }) => {
      await navigator.share({ url, title, text });
    },
  },
  {
    key: "copy",
    label: "Copy link",
    icon: Link2,
    handler: async ({ url, setCopied }) => {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    },
  },
  {
    key: "whatsapp",
    label: "WhatsApp",
    icon: WhatsAppIcon,
    handler: ({ url, text }) => {
      const body = `${text}\n${url}`;
      window.open(
        `https://api.whatsapp.com/send?text=${encodeURIComponent(body)}`,
        "_blank",
        "noopener,noreferrer",
      );
    },
  },
  {
    key: "x",
    label: "Post on X",
    icon: XIcon,
    handler: ({ url, text }) => {
      const intent = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
      window.open(intent, "_blank", "noopener,noreferrer");
    },
  },
];

export const ShareMenu = ({ searchId, title, text }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const wrapperRef = useRef(null);

  const shareUrl =
    typeof window !== "undefined" && searchId
      ? `${window.location.origin}/api/share/${searchId}`
      : "";

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", close);
    return () => window.removeEventListener("mousedown", close);
  }, [open]);

  const onPick = async (channel) => {
    if (!shareUrl) return;
    // First share click is what actually publishes the page — fire the
    // tracking call so the backend flips the public-share gate.
    if (searchId) markShared(searchId).catch(() => {});
    try {
      await channel.handler({
        url: shareUrl,
        title: title || "Should I Buy It?",
        text: text || title || "I just got a clear buying verdict on this:",
        setCopied,
      });
    } catch {
      /* user dismissed share sheet; nothing to do */
    }
    if (channel.key !== "copy") setOpen(false);
  };

  const visible = CHANNELS.filter((c) => !c.onlyIf || c.onlyIf());

  if (!searchId) return null;

  return (
    <div ref={wrapperRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="rec-share-trigger"
        className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-border hover:bg-secondary hover:border-emerald-500/40 text-muted-foreground hover:text-foreground transition"
      >
        {copied ? (
          <Check className="w-3.5 h-3.5 text-emerald-500" />
        ) : (
          <Share2 className="w-3.5 h-3.5" />
        )}
        {copied ? "Link copied" : "Share"}
      </button>

      {open && (
        <div
          data-testid="rec-share-menu"
          className="absolute right-0 mt-2 w-52 rounded-xl border border-border bg-popover shadow-xl backdrop-blur z-50 overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150"
          role="menu"
        >
          <div className="px-3 py-2 border-b border-border/60">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              Share recommendation
            </div>
          </div>
          <ul>
            {visible.map((c) => {
              const Icon = c.icon;
              return (
                <li key={c.key}>
                  <button
                    onClick={() => onPick(c)}
                    data-testid={`rec-share-${c.key}`}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm hover:bg-secondary hover:text-foreground text-foreground/85 transition text-left"
                    role="menuitem"
                  >
                    <Icon className="w-3.5 h-3.5 text-emerald-500" />
                    {c.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ShareMenu;
